import { siteApi } from "@api";
import useAuthSession from "@hooks/useAuthSession";
import usePaymentInfo from "@hooks/usePaymentInfo";
import { BankAccount } from "@types";
import { FundingProfileType, MemberBankFundingType } from "kz-ui-sdk";
import { useMemo } from "react";
import useUserMode from "./useUserMode";

interface UseFundingProfileProps {
  skip?: boolean;
}

/**
 * Hook to get the funding profile list and configs
 */
const useFundingProfile = ({ skip = false }: UseFundingProfileProps = {}) => {
  const { isGuest } = useUserMode();

  const {
    data: fundingProfileListData,
    isFetching: isFetchingFundingProfileList,
    isLoading: isLoadingFundingProfileList,
  } = siteApi.useGetFundingProfileListQuery(
    {
      query: {
        sort: "createdAt:desc",
        limit: 1000,
        offset: 0,
      },
    },
    {
      skip: isGuest || skip,
    },
  );
  const { self } = useAuthSession();

  const { getPaymentMethodByKey, paymentMethods } = usePaymentInfo();

  const [createFundingProfile, { isLoading: isCreatingFundingProfile }] = siteApi.useCreateFundingProfileMutation();

  const [updateFundingProfile, { isLoading: isUpdatingFundingProfile }] = siteApi.useUpdateFundingProfileMutation();

  const [setFundingProfileDefault, { isLoading: isSettingFundingProfileDefault }] =
    siteApi.useSetFundingProfileDefaultMutation();

  const fundingProfileList = useMemo(() => {
    return (
      fundingProfileListData?.entries?.map((item) => {
        return {
          ...item,
          icon: paymentMethods[item.key]?.icon,
          name: paymentMethods[item.key]?.name,
          accountName: item.info?.accountName ?? self?.member?.fullname ?? "",
          displayName: paymentMethods[item.key]?.displayName,
          ...getPaymentMethodByKey(item.key, item?.info?.bankType as MemberBankFundingType),
        };
      }) || []
    );
  }, [paymentMethods, fundingProfileListData?.entries, self, getPaymentMethodByKey]);

  const defaultFundingProfile = useMemo(() => {
    return fundingProfileList?.find((item) => item.isPrimary);
  }, [fundingProfileList]);

  const defaultBankAccount: BankAccount | null = useMemo(() => {
    const fundingProfile = fundingProfileList?.find((item) => item.isPrimary && item.type === FundingProfileType.Bank);
    if (fundingProfile) {
      return {
        bankAccNo: fundingProfile.accNo,
        bankAccName: fundingProfile.accountName,
        bankKey: fundingProfile.key,
        bankName: fundingProfile.name,
        bankIcon: fundingProfile.icon || null,
      };
    }
    return null;
  }, [fundingProfileList]);

  const defaultEWallet: (typeof fundingProfileList)[number] | null = useMemo(() => {
    const fundingProfile = fundingProfileList?.find(
      (item) => item.isPrimary && item.type === FundingProfileType.EWallet,
    );
    if (fundingProfile) {
      return fundingProfile;
    }
    return null;
  }, [fundingProfileList]);

  return {
    defaultFundingProfile,
    defaultBankAccount,
    defaultEWallet,
    isFetchingFundingProfileList,
    isLoadingFundingProfileList,
    isCreatingFundingProfile,
    fundingProfileList,
    createFundingProfile,
    isUpdatingFundingProfile,
    updateFundingProfile,
    isSettingFundingProfileDefault,
    setFundingProfileDefault,
  };
};

export default useFundingProfile;
